import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomButton from "../components/Button";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CanvasImage from "../components/canvas";
import {
  getDetectionImageCandidates,
  getDetectionRenderSpec,
  getFindings,
  toCanvasBoxes,
} from "../utils/detectionImage";
const getContrastTextColor = (bgColor) => {
  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 125 ? "black" : "white";
};

const getInitials = (name) => {
  if (!name) return "NA";
  const parts = name.split(" ");
  return parts
    .map((p) => p[0])
    .join("")
    .toUpperCase();
};

const TestPage = () => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const auth = localStorage.getItem("token");
  const locationCheck = useLocation();
  const {
    tid,
    detections: hardTissueDetections,
    softTissueDetections,
    testType,
    uid,
    name,
    kioskId,
    doctor,
  } = locationCheck.state || {};
  // Soft tissue scans keep their findings in softTissueData, not detections.
  // Angles with findings come first so the page opens on one of them.
  const detections = useMemo(() => {
    const list =
      (testType === "SOFT_TISSUE" ? softTissueDetections : hardTissueDetections) || [];
    return [...list].sort((a, b) => getFindings(b).length - getFindings(a).length);
  }, [testType, softTissueDetections, hardTissueDetections]);
  const [popupImage, setPopupImage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [images, setImages] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [visibleDiseases, setVisibleDiseases] = useState({});
  const toggleDisease = (name) => {
    setVisibleDiseases((prev) => ({
      ...prev,
      [tabIndex]: {
        ...(prev[tabIndex] || {}),
        [name]: !(prev[tabIndex] || {})[name],
      },
    }));
  };

  const handleOpenPopup = async (baseFileUrl) => {
    try {
      const img = await apiService.getImage(auth, baseFileUrl);
      setPopupImage(img);
      setOpenDialog(true);
    } catch (error) {
      console.error("Error fetching popup image:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPopupImage(null);
  };
  useEffect(() => {
    const fetchImages = async () => {
      if (!detections || detections.length === 0) return;

      try {
        const results = await Promise.all(
          detections.map(async (det) => {
            // Prefer a clean image and draw boxes ourselves; fall back per
            // detection so one missing file does not blank the others.
            for (const candidate of getDetectionImageCandidates(det)) {
              try {
                const url = await apiService.getImage(auth, candidate.url);
                const img = new Image();
                img.src = url;
                await img.decode(); // Wait until fully ready
                return {
                  img,
                  mode: candidate.mode,
                  ...getDetectionRenderSpec(candidate, img),
                };
              } catch (err) {
                console.warn(`Could not load ${candidate.mode} image`, err);
              }
            }
            return { error: true };
          })
        );

        setImages(results);

        const initialVisible = {};
        detections.forEach((det, idx) => {
          initialVisible[idx] = {};
          getFindings(det).forEach((disease) => {
            initialVisible[idx][disease.name] = true;
          });
        });
        setVisibleDiseases(initialVisible);
      } catch (error) {
        console.error("Error fetching or decoding images:", error);
      }
    };

    fetchImages();
  }, [detections, auth]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  if (!detections || detections.length === 0) {
    return (
      <Box p={2}>
        <Typography>No detections found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", typography: "body1", mt: 2 }}>
      {/* Profile Section */}
      <Box
        display="flex"
        alignItems="center"
        p={2}
        sx={{
          border: "1px solid #eee",
          borderRadius: "10px",
          mb: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Avatar
          sx={{
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.primary.main,
            color: theme.palette.getContrastText(
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.primary.main
            ),
            width: 60,
            height: 60,
            fontSize: 24,
            mr: 2,
          }}
        >
          {getInitials(name)}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {name || "Unknown Name"}
          </Typography>
          <Typography variant="body2" color="textPrimary">
            Associate Doctor: {doctor || "-"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Kiosk ID: {kioskId || "-"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            User ID: {uid || "-"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Test ID: {tid || "-"}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Detection Tabs"
        sx={{
          borderBottom: "1px solid #eee",
          ".MuiTab-root": {
            textTransform: "none",
            fontWeight: "500",
          },
          ".Mui-selected": {
            color: "#7c3aed !important",
          },
          ".MuiTabs-indicator": {
            backgroundColor: "#7c3aed",
          },
        }}
      >
        {detections.map((detection, index) => (
          <Tab
            key={index}
            label={
              getFindings(detection).length
                ? `Angle: ${detection.angle} (${getFindings(detection).length})`
                : `Angle: ${detection.angle}`
            }
          />
        ))}
      </Tabs>

      {/* Image Viewer */}

      <Box
        marginTop={4}
        width="100%"
        height="100%"
        display="flex"
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CustomButton
          text="Get Base Image"
          onClick={() => handleOpenPopup(detections[tabIndex]?.baseFileUrl)}
          width="200px"
        />
      </Box>
      <Box
        mt={3}
        p={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {images[tabIndex]?.error ? (
          <Typography color="textSecondary">Image unavailable.</Typography>
        ) : images[tabIndex] ? (
          <Box>
            <CanvasImage
              key={tabIndex}
              imageObj={images[tabIndex].img}
              crop={images[tabIndex].crop}
              maxWidth={600}
              maxHeight={700}
              coordinates={
                images[tabIndex].drawBoxes
                  ? toCanvasBoxes(
                      getFindings(detections[tabIndex]).filter(
                        (item) =>
                          visibleDiseases[tabIndex]?.[item.name] !== false
                      ),
                      images[tabIndex].boxScale
                    )
                  : []
              }
            />
            {images[tabIndex].mode === "legacy" && (
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                textAlign="center"
                mt={1}
              >
                Clean image not available for this angle, showing the
                pre-marked disease image.
              </Typography>
            )}

            <Box
              mt={2}
              display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="center"
            >
              {getFindings(detections[tabIndex]).map((item) => {
                const bg = item.color;
                const textColor = getContrastTextColor(bg);
                const isActive =
                  visibleDiseases[tabIndex]?.[item.name] !== false;

                return (
                  <CustomButton
                    key={item._id || item.key || item.name}
                    text={item.name}
                    onClick={() => toggleDisease(item.name)}
                    backgroundColor={bg}
                    color={textColor}
                    hoverColor={bg}
                    width="auto"
                    style={{
                      opacity: isActive ? 1 : 0.5,
                      fontWeight: "bold",
                      paddingInline: "12px",
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ) : (
          <CircularProgress />
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          {popupImage ? (
            <img
              src={popupImage}
              alt="Base"
              style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}
            />
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TestPage;
